// src/pages/Dashboard/Admin/AdherentPret/components/AddPretDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaBox, FaCalendarAlt, FaSave, FaTimes, FaUser } from "react-icons/fa";

import {
  getArticlePhotoSrc,
  todayStr,
} from "../utils/adherentPretDashboardUtils";

const initialForm = {
  adherent_id: "",
  article_id: "",
  date_pret: todayStr(),
  note_pret: "",
};

export default function AddPretDrawer({
  isOpen,
  onClose,
  onSubmit,
  adherents = [],
  articlesPret = [],
  loadingMeta,
  saving,
}) {
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      ...initialForm,
      date_pret: todayStr(),
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const selectedArticle = useMemo(() => {
    return articlesPret.find(
      (article) => String(article.id) === String(formData.article_id)
    );
  }, [articlesPret, formData.article_id]);

  const selectedAdherent = useMemo(() => {
    return adherents.find(
      (adherent) => String(adherent.id) === String(formData.adherent_id)
    );
  }, [adherents, formData.adherent_id]);

  const canSubmit =
    !loadingMeta &&
    !saving &&
    formData.adherent_id &&
    formData.article_id &&
    formData.date_pret;

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouveau prêt
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter un prêt de matériel
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sélectionnez l’adhérent, le matériel et la date de prêt.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          id="add-pret-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div>
            <label className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FaUser />
              Adhérent
            </label>

            <select
              value={formData.adherent_id}
              onChange={(event) => updateField("adherent_id", event.target.value)}
              disabled={loadingMeta}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
              autoFocus
            >
              <option value="">
                {loadingMeta ? "Chargement..." : "— Sélectionner un adhérent —"}
              </option>

              {adherents.map((adherent) => (
                <option key={adherent.id} value={adherent.id}>
                  {adherent.nom} {adherent.prenom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FaBox />
              Matériel
            </label>

            <select
              value={formData.article_id}
              onChange={(event) => updateField("article_id", event.target.value)}
              disabled={loadingMeta}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            >
              <option value="">
                {loadingMeta ? "Chargement..." : "— Sélectionner un matériel —"}
              </option>

              {articlesPret.map((article) => (
                <option key={article.id} value={article.id}>
                  {article.nom}
                </option>
              ))}
            </select>
          </div>

          {selectedArticle || selectedAdherent ? (
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
              <div className="flex items-start gap-4">
                {selectedArticle ? (
                  <img
                    src={getArticlePhotoSrc(selectedArticle.photo_url)}
                    alt={selectedArticle.nom || "Matériel"}
                    className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    onError={(event) => {
                      event.currentTarget.src = "/img/no-image.jpg";
                    }}
                  />
                ) : null}

                <div className="min-w-0">
                  {selectedAdherent ? (
                    <>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Adhérent
                      </p>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {selectedAdherent.nom} {selectedAdherent.prenom}
                      </p>
                    </>
                  ) : null}

                  {selectedArticle ? (
                    <div className="mt-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Matériel
                      </p>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {selectedArticle.nom}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {selectedArticle.categorie || "—"}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <FaCalendarAlt />
                Date de prêt
              </label>

              <input
                type="date"
                value={formData.date_pret}
                onChange={(event) => updateField("date_pret", event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Note
              </label>

              <input
                type="text"
                value={formData.note_pret}
                onChange={(event) => updateField("note_pret", event.target.value)}
                placeholder="Taille, état, accessoires..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              form="add-pret-form"
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}